import { atom, type WritableAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { sum } from 'lodash-es';

import searchAtomPair from 'atoms/search-atom';
import { getMapDefaultConfig } from 'components/map/atoms';
import { mutationAtom, mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  EntityBaseResponse,
  PackagesQueryResponse,
} from 'types/api-responses';

import type {
  AssetSummary,
  CreateAssetResponse,
  CreateDraftPayload,
  DraftRefusalBody,
  CreateLtdSlotAssetPayload,
  CreatePackageResponse,
  CreatePackageWizardPayload,
  CreateWellboreAssetPayload,
  DraftAssetsQueryParams,
  DraftAssetsResponse,
  DraftAssetsUpdatePayload,
  DraftAssetsUpdateResponse,
  DraftMapAssetFeature,
  DraftMapAssetFeatureWithSelection,
  DraftMapDevelopmentAreaFeature,
  DraftMapFeaturesResponse,
  DraftMapSlotFeature,
  DraftRebuildFailure,
  DraftResponse,
  DraftSlotsQueryParams,
  DraftSlotsResponse,
  EditPackageDetail,
  EntitySearchParams,
  PackageSearchParams,
  PackageSelectAllRow,
  SavePackagePayload,
  SlotSelectionMutationPayload,
  SlotSelectionResponse,
  SlotSummary,
  SourceSelectAllParams,
  SourceSelectAllResponse,
  SourceSelectAllRow,
  LargeTableConfirmation,
  UpdateDraftPayload,
  VerificationState,
} from './types';
import {
  EMPTY_ASSET_NAME_CHECK,
  type AssetNameCheck,
} from './asset-list-utils';
import { draftAssetRowId, draftRefusal } from './utils';

/** Latest Step 1 summary snapshot rendered by `Step1SummaryPanel`. */
export const currentDraftSummaryAtom = atom<{
  asset: AssetSummary;
  slot: SlotSummary;
} | null>(null);

/**
 * Total selected-asset count — sum across `asset.byAssetType`. Read by
 * the Step 1 summary panel header and the Step 1 Next-button gate.
 * Returns 0 when the summary is null (pre-first-fetch) so gates default
 * to disabled.
 */
export const draftSelectedAssetCountAtom = atom((get) => {
  const summary = get(currentDraftSummaryAtom);
  return sum(Object.values(summary?.asset.byAssetType ?? {}));
});

/**
 * Total selected-slot count — sum across `slot.byWellStatus` (every
 * dimension sums to the same total). Read by the Step 1 summary panel
 * header.
 */
export const draftSelectedSlotCountAtom = atom((get) => {
  const summary = get(currentDraftSummaryAtom);
  return sum(Object.values(summary?.slot.byWellStatus ?? {}));
});

/**
 * Entity search atom powering Step 0's "From Entity" source picker.
 * POSTs to /queries/entities. Feature-local instance so the Step 2
 * acquiring-entity picker (below) can't collide with Step 0's paging /
 * search state when the user back-navigates between steps.
 *
 * Typed with `EntityBaseResponse` because `searchAtomPair`'s `Data`
 * generic is constrained to the shared `ApiQueryResponse` union. The
 * wire response also carries classification + count fields not on the
 * base type; consumers narrow to `EntityRow` at the read site.
 */
export const packageSourceEntitySearchAtom = searchAtomPair<
  EntityBaseResponse,
  EntitySearchParams
>('entities');

/**
 * Shared type for the package wizard's entity `searchAtomPair` instances.
 * Declared here (in a `.ts` module) because `typeof searchAtomPair<...>`
 * inside a `.tsx` file is unparseable — the leading `<` reads as JSX.
 * Consumed by `PackageEntityTable` to type its injected `searchAtom` prop.
 */
export type PackageEntitySearchAtomPair = typeof packageSourceEntitySearchAtom;

/**
 * Entity search atom powering Step 2's acquiring-entity drawer picker.
 * Mirrors {@link packageSourceEntitySearchAtom} but scoped to its own
 * paging / search state so opening the drawer doesn't inherit whatever
 * page / filter Step 0 last left the source table on (and vice versa).
 */
export const packageAcquirerEntitySearchAtom = searchAtomPair<
  EntityBaseResponse,
  EntitySearchParams
>('entities');

/**
 * Package search atom powering Step 0's "From Existing Package" source
 * picker. POSTs to /queries/packages. Consumers narrow to `PackageRow`
 * at the read site; the shared response type carries `id: string` while
 * the wizard's `PackageRow.id` is a `number`, matching the integer PK
 * the jbuilder emits.
 */
export const packageSourceSearchAtom = searchAtomPair<
  PackagesQueryResponse,
  PackageSearchParams
>('packages');

/**
 * Bulk id fetch behind Step 0's "select all" header checkbox. POSTs the
 * table's current search / filter / sort params (minus paging) and gets back
 * every matching row — the ids the user just selected include rows off the
 * current page, so the table can't name them from its own data.
 *
 * URLs are hardcoded for the same reason `createPackageDraftAtom`'s is: the
 * API root's `queries.*` map carries the query endpoints themselves, not
 * their collection actions.
 *
 * One atom per resource, because Step 0 keeps BOTH source tables mounted
 * (the tile toggles visibility with CSS) and a shared atom would cross the
 * two tables' in-flight state.
 *
 * @param {string} url - `select_all` collection action for the resource
 * @return {WritableAtom} Mutation atom for that endpoint
 */
const buildSourceSelectAllAtom = <TRow extends SourceSelectAllRow>(
  url: string
): SourceSelectAllAtom<TRow> =>
  mutationAtom<SourceSelectAllResponse<TRow>, [SourceSelectAllParams]>(
    async (_get, _set, params: SourceSelectAllParams) => {
      const response = await request.post<
        SourceSelectAllResponse<TRow>,
        { errors: string[] }
      >(url, params, new AbortController());
      if (response.type === 'success') return response.data;
      if (response.type === 'error' && response.data?.errors?.length) {
        throw new Error(response.data.errors.join(', '));
      }
      throw new Error('Failed to fetch the full selection');
    }
  );

/** Shared type for the two `select_all` atoms, by the row each returns. */
export type SourceSelectAllAtom<TRow extends SourceSelectAllRow> = WritableAtom<
  Promise<SourceSelectAllResponse<TRow>> | null,
  [SourceSelectAllParams],
  Promise<SourceSelectAllResponse<TRow>>
>;

export const entitySourceSelectAllAtom =
  buildSourceSelectAllAtom<SourceSelectAllRow>('/queries/entities/select_all');

export const packageSourceSelectAllAtom =
  buildSourceSelectAllAtom<PackageSelectAllRow>('/queries/packages/select_all');

/**
 * Create-draft mutation fired by Step 0's Next click. POSTs to
 * `/packages/drafts`; on success, the step writes `draft.id` into form
 * state so downstream steps can query slots for it. Surfaces the BE's
 * 422 validation messages verbatim when present so the user sees the
 * specific constraint that failed (missing as_of_date, multiple base
 * sources, etc.) rather than a generic "failed to create draft". A table
 * above the warning size is refused as a `LargeTableRefusal`, with its count.
 *
 * The wizard's `handleNext` awaits the returned promise and only
 * advances on resolution — throwing here both blocks navigation and
 * surfaces via the loadable state's `hasError` branch for inline UI.
 *
 * Endpoint URL is hardcoded (not resolved via the API root resources
 * map) because `/packages/drafts` is a resource endpoint rather than a
 * query endpoint — it has no entry under `queries.*` in the
 * `ApiResourcesResponse` map.
 */
export const [createPackageDraftAtom, createPackageDraftLoadableAtom] =
  mutationAtomWithLoadable<
    DraftResponse,
    [CreateDraftPayload & LargeTableConfirmation]
  >(
    async (
      _get,
      _set,
      payload: CreateDraftPayload & LargeTableConfirmation
    ) => {
      const response = await request.post<DraftResponse, DraftRefusalBody>(
        '/packages/drafts',
        payload,
        new AbortController()
      );
      if (response.type === 'success') return response.data;
      if (response.type === 'error' && response.data?.errors?.length) {
        throw draftRefusal(response.data);
      }
      throw new Error('Failed to create draft');
    }
  );

/**
 * Update-draft mutation. PATCH `/packages/drafts/:draftId` to flip
 * scope (`asOfDate`, `sellerEntityIds`, `packageIds`). Same async
 * lifecycle as create — 202 with `status: 'materializing'`, then
 * Pusher `status_change`. The wizard api consumes both atoms behind a
 * single `upsertDraft` surface.
 *
 * BE rejects empty PATCHes with `at_least_one_change` (422); the FE
 * keeps them off the wire via the form's dirty state.
 */
export const [updatePackageDraftAtom, updatePackageDraftLoadableAtom] =
  mutationAtomWithLoadable<
    DraftResponse,
    [{ draftId: string; payload: UpdateDraftPayload & LargeTableConfirmation }]
  >(async (_get, _set, { draftId, payload }) => {
    const response = await request.patch<DraftResponse, DraftRefusalBody>(
      `/packages/drafts/${draftId}`,
      payload,
      new AbortController()
    );
    if (response.type === 'success') return response.data;
    if (response.type === 'error' && response.data?.errors?.length) {
      throw draftRefusal(response.data);
    }
    throw new Error('Failed to update draft');
  });

/** No tick answered since the last `maps/features` read. */
const NO_FLIPS: ReadonlyMap<string, DraftMapAssetFeature | null> = new Map();

/**
 * The Step 1 map's ticks since `maps/features` was last read, by asset id: a
 * Tract ticked (its outline, from its Assets table row) or unticked (null) by a
 * tick the server answered 200. The map draws them so, on whatever page the
 * table is on, while the read the tick starts is on its way. Cleared as each
 * read starts: the server answers it with the ticks already applied.
 */
const draftMapSelectionFlipsAtom = atom(NO_FLIPS);

/**
 * Map-features fetch for the wizard map. GET
 * `/packages/drafts/:draft_id/maps/features`. Triggered explicitly from
 * the wizard — Step 1 / Step 2 parents fire on mount with a draftId,
 * and Step 1 again after each settled change to the draft (`draftReloadAtom`).
 * Each read aborts the one it replaces, so a run of changes leaves one in
 * flight. `null` drops the answer and any read in flight: each step does so as
 * it closes. Back to Select Source, which shows no wizard map, and Next again
 * can rebuild the draft, so Step 1 then waits for its own read rather than
 * drawing, and fitting the camera to, what it read before. (Between Step 1 and
 * Summary the arriving map still draws the leaving step's answer first; that
 * is the same draft's selection, as neither step leaves with a change unsent.)
 * Selection state is NOT in the response — the wizard's `selected`
 * styling comes from the slot table's per-row `selected` flag.
 */
export const [draftMapFeaturesAtom, draftMapFeaturesLoadableAtom] =
  mutationAtomWithLoadable<DraftMapFeaturesResponse | null, [string | null]>(
    (() => {
      let controller: AbortController | null = null;
      return async (_get, set, draftId: string | null) => {
        controller?.abort();
        set(draftMapSelectionFlipsAtom, NO_FLIPS);
        if (draftId === null) return null;
        const current = new AbortController();
        controller = current;
        const response = await request.get<DraftMapFeaturesResponse>(
          `/packages/drafts/${draftId}/maps/features`,
          current
        );
        // A newer read owns the loadable; settling this one would only raise
        // an unhandled rejection from the caller that fired it.
        if (current.signal.aborted) return new Promise<never>(() => {});
        if ('error' in response) {
          throw new Error(
            typeof response.error === 'string'
              ? response.error
              : 'Failed to load map features'
          );
        }
        return response;
      };
    })()
  );

/**
 * A query of one of a draft's tables, POSTed to
 * `/queries/packages/drafts/:id/<table>`. Each call aborts the one before it,
 * so rapid page or search changes can't settle out of order, and an aborted
 * call never settles: a newer call owns the loadable, and throwing would flash
 * a spurious error banner. Each query made here has its own abort controller,
 * so two tables never cancel each other's reads.
 *
 * @param {string} table - The draft table: `assets` or `slots`
 * @param {string} fallback - The error when a failure gives no reasons
 * @return {Function} The query, from its params to the response
 */
const draftTableQuery = <Result, Params extends { draftId: string }>(
  table: 'assets' | 'slots',
  fallback: string
) => {
  let controller: AbortController | null = null;
  return async ({ draftId, ...body }: Params): Promise<Result> => {
    controller?.abort();
    controller = new AbortController();
    const response = await request.post<Result, { errors: string[] }>(
      `/queries/packages/drafts/${draftId}/${table}`,
      body,
      controller
    );
    if (response.type === 'success') return response.data;
    if (response.type === 'abort') return new Promise<never>(() => {});
    if (response.type === 'error' && response.data?.errors?.length) {
      throw new Error(response.data.errors.join(', '));
    }
    throw new Error(fallback);
  };
};

const fetchDraftAssets = draftTableQuery<
  DraftAssetsResponse,
  DraftAssetsQueryParams
>('assets', 'Failed to fetch draft assets');

/**
 * Step 1 assets query. POST `/queries/packages/drafts/:id/assets`. Each
 * call aborts the previous in-flight request so rapid page / search
 * changes don't produce out-of-order results. Step 2's read-only variant
 * has its own atom (`step2-assets-table.tsx`) so the two steps can't
 * cross-cancel each other's requests.
 *
 * Lives in `atoms.ts` (not the table file) because the wizard map also
 * reads the loadable to merge per-row geometry with the
 * `maps/features` selected universe.
 */
export const [draftAssetsQueryAtom, draftAssetsQueryLoadableAtom] =
  mutationAtomWithLoadable<DraftAssetsResponse, [DraftAssetsQueryParams]>(
    async (_get, set, params) => {
      const data = await fetchDraftAssets(params);
      set(currentDraftSummaryAtom, {
        asset: data.assetSummary,
        slot: data.slotSummary,
      });
      return data;
    }
  );

/** A draft selection PATCH's failure body: validation errors, or a 409's reason. */
type DraftChangeFailure = { errors?: string[]; message?: string };

/**
 * The error a draft selection PATCH failed with: its validation errors, else the reason a 409 for
 * a draft still building gives in `message`, else `fallback`.
 *
 * @param {object} response - The PATCH's response, any but a success
 * @param {string} fallback - The message when the response gives no reason
 * @return {Error} The error to throw
 */
const draftChangeError = (
  response: { type: string; data?: DraftChangeFailure },
  fallback: string
): Error => {
  const failure = response.type === 'error' ? response.data : undefined;
  if (failure?.errors?.length) return new Error(failure.errors.join(', '));
  return new Error(failure?.message ?? fallback);
};

/**
 * Draft-assets PATCH atom. Accepts incremental
 * `{addedAssetIds, removedAssetIds}` (per-row clicks) or bulk
 * `{selectAll, filters?}` (header click). Either shape resolves through
 * the same add / remove machinery; the slots table is stale on either
 * path and must be refetched.
 *
 * A tick or untick of an asset in the table, one or all, changes only the
 * selection: 200, complete on return. A change of the Subtracts of a Tract
 * held in part, an untick of that Tract, or a Tract joining the table answers
 * 202 with `addedRowCount === null`: the rows are built after the response,
 * so nothing may be read until the build reports back. Step 1 sends every
 * change through `useDraftAssetChange`, which waits for that build and then
 * has the step re-read what it shows.
 */
export const [updateDraftAssetsAtom, updateDraftAssetsLoadableAtom] =
  mutationAtomWithLoadable<
    DraftAssetsUpdateResponse,
    [DraftAssetsUpdatePayload]
  >(async (_get, set, payload) => {
    const { draftId, ...body } = payload;
    const response = await request.patch<
      DraftAssetsUpdateResponse,
      DraftChangeFailure
    >(`/packages/drafts/${draftId}/assets`, body, new AbortController());
    if (response.type === 'success') {
      set(currentDraftSummaryAtom, {
        asset: response.data.assetSummary,
        slot: response.data.slotSummary,
      });
      return response.data;
    }
    throw draftChangeError(response, 'Failed to update draft assets');
  });

/**
 * `POST /entities/:entity_id/assets` for the Add Wellbore and Add LTD modals,
 * attaching the new asset to the draft when `draftId` is given. A 422's
 * `details` or `errors[]` become the thrown message, for the modal's banner.
 *
 * @param {object} payload - The entity, the draft if any, and the asset
 * @param {string} fallback - The error when a failure gives no reasons
 * @return {Promise<CreateAssetResponse>} The created asset
 */
const createEntityAsset = async (
  {
    entityId,
    draftId,
    asset,
  }: CreateWellboreAssetPayload | CreateLtdSlotAssetPayload,
  fallback: string
): Promise<CreateAssetResponse> => {
  const url = draftId
    ? `/entities/${entityId}/assets?draft_id=${encodeURIComponent(draftId)}`
    : `/entities/${entityId}/assets`;
  const response = await request.post<
    CreateAssetResponse,
    { errors?: string[]; details?: Record<string, string> }
  >(url, { asset }, new AbortController());
  if (response.type === 'success') return response.data;
  // 422 details shape from `Entities::AssetsController#model_errors`:
  // { error: '...', details: { field: 'msg', ... } }. Flatten into a
  // single message for the banner.
  if (response.type === 'error' && response.data?.details) {
    throw new Error(
      Object.entries(response.data.details)
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ')
    );
  }
  if (response.type === 'error' && response.data?.errors?.length) {
    throw new Error(response.data.errors.join(', '));
  }
  throw new Error(fallback);
};

/**
 * `POST /entities/:entity_id/assets` — creates a non-tract asset and, when
 * `draftId` is supplied, ticks it in the user's draft (its id joins the draft's
 * `asset_ids`), its `package_draft_id` marking it as created in that draft's
 * wizard session and not yet saved into a package.
 * Powers the WBA Add Wellbore (INS-9039) and Add LTD (INS-9040) modals.
 *
 * The BE always creates a new row (D1 idempotency was dropped in INS-9156 so
 * the user can attach the same UWI/NRI to a draft even when a matching
 * ownership row exists elsewhere). 201 on success; 422 surfaces `errors[]`.
 *
 * Caller responsibilities:
 *   - Refetch slots/assets after success — the modal-host wires this.
 *   - Display `errors[]` from the thrown Error.message in the banner.
 */
export const [createWellboreAssetAtom, createWellboreAssetLoadableAtom] =
  mutationAtomWithLoadable<CreateAssetResponse, [CreateWellboreAssetPayload]>(
    (_get, _set, payload) =>
      createEntityAsset(payload, 'Failed to create wellbore asset')
  );

/**
 * Sibling to {@link createWellboreAssetAtom} for the WBA Add LTD modal
 * (INS-9040). Same endpoint, same 201 / 422 shape; differs only in the
 * `asset.kind` and the slot-flavored payload fields.
 *
 * `slotId` here is the numeric `inventory_unit_slots` PK as a string
 * (Asset::LTDSlot's `slot_id` column is text-typed but validates
 * `~ /\A\d+\z/`). The query that produces it (LTDCapable)
 * carries `inventorySlotId` as a number; the modal stringifies before
 * submitting.
 */
export const [createLtdSlotAssetAtom, createLtdSlotAssetLoadableAtom] =
  mutationAtomWithLoadable<CreateAssetResponse, [CreateLtdSlotAssetPayload]>(
    (_get, _set, payload) =>
      createEntityAsset(payload, 'Failed to create LTD asset')
  );

/**
 * Edit-mode hydration fetch. `GET /packages/:id` — body shape mirrors
 * `_package.json.jbuilder` (camelCased per the global jbuilder config).
 * `mutationAtomWithLoadable` gives us the id-arg signature
 * `lazyAtomWithLoadable` lacks. The page-level loading gate awaits
 * this before mounting the form.
 */
export const [editPackageDetailAtom, editPackageDetailLoadableAtom] =
  mutationAtomWithLoadable<EditPackageDetail, [string]>(
    async (_get, _set, packageId: string) => {
      const response = await request.get<EditPackageDetail>(
        `/packages/${packageId}`
      );
      if ('error' in response) {
        throw new Error(
          typeof response.error === 'string'
            ? response.error
            : 'Failed to load package'
        );
      }
      return response;
    }
  );

const fetchDraftSlots = draftTableQuery<
  DraftSlotsResponse,
  DraftSlotsQueryParams
>('slots', 'Failed to fetch draft properties');

/**
 * Draft-slots query atom for Step 1's slots table. POSTs
 * `/queries/packages/drafts/:draft_id/slots` with a camelCase body of
 * pagination/filter/sort params. The controller runs
 * `deep_transform_keys { underscore }` before `permit`, so the client
 * sends camelCase directly — no per-request snake_case conversion.
 *
 * Each call aborts the previous in-flight request so rapid page/search
 * changes don't produce out-of-order results. Throws on non-success so
 * the table can surface the error via the loadable state.
 *
 * Its abort controller is `fetchDraftSlots`'s, made once at module load and
 * shared by every caller of this atom: only Step 1's `SlotsTable` calls it. A
 * second table needs a query of its own (see
 * `createReadOnlyDraftSlotsQueryAtoms`).
 */
export const [draftSlotsQueryAtom, draftSlotsQueryLoadableAtom] =
  mutationAtomWithLoadable<DraftSlotsResponse, [DraftSlotsQueryParams]>(
    async (_get, set, params) => {
      const data = await fetchDraftSlots(params);
      set(currentDraftSummaryAtom, {
        asset: data.assetSummary,
        slot: data.slotSummary,
      });
      return data;
    }
  );

/**
 * A read-only draft slots query atom, for one of Step 2's tables. Its abort
 * controller is its own, so paging Step 1's tables can't cancel Step 2's read
 * (and vice versa), and it skips the `currentDraftSummaryAtom` write: Step 1's
 * summary panel shows the whole draft, and a `selected: true` read would shift
 * its breakdowns to the selected rows only.
 *
 * @return {Array} A new [baseAtom, loadableAtom] pair
 */
export const createReadOnlyDraftSlotsQueryAtoms = () => {
  const fetchSlots = draftTableQuery<DraftSlotsResponse, DraftSlotsQueryParams>(
    'slots',
    'Failed to fetch draft properties'
  );
  return mutationAtomWithLoadable<DraftSlotsResponse, [DraftSlotsQueryParams]>(
    (_get, _set, params) => fetchSlots(params)
  );
};

const NO_ASSET_FEATURES: DraftMapAssetFeatureWithSelection[] = [];
const NO_SLOT_FEATURES: DraftMapSlotFeature[] = [];
const NO_DA_FEATURES: DraftMapDevelopmentAreaFeature[] = [];

/**
 * Wizard-map view model — merges per-row geometry from the Step 1 asset
 * + slot table loadables (opt-in via `includeGeometry: true`) with the
 * selected-only `maps/features` payload. On id overlap, the features
 * (selected) entry wins.
 *
 * `isLoading` is true while any feeding source is in flight or before
 * the canonical features payload has landed; `features` is the raw
 * selected universe and drives the unfocused camera fit.
 */
export interface WizardMapData {
  assets: DraftMapAssetFeatureWithSelection[];
  slots: DraftMapSlotFeature[];
  developmentAreas: DraftMapDevelopmentAreaFeature[];
  features: DraftMapFeaturesResponse | null;
  isLoading: boolean;
}

// Stabilizers used to keep the merged arrays' (and the wrapper object's)
// referential identity stable across recomputes when the content is
// unchanged. Without these, every table sort / page / refetch produces
// fresh refs — which busts the wizard map's `collections` useMemo and
// triggers a full Mapbox redraw even when the map's view hasn't changed.
//
// Each stabilizer fingerprints its input; matching fingerprint → return
// the previous reference. Fingerprint fields encode the content the map
// actually paints — id+selected+the stored Subtracts a Tract held in part is
// drawn from for assets (a tick reshapes the Tract under the same id),
// id+geometry-presence for slots (`includeGeometry` toggling between fetches
// must invalidate), name for development areas. Field separator is `` so a literal
// comma or pipe in any value (e.g., a unit name with punctuation)
// can't collide.
const STABILIZER_DELIMITER = '';
const makeArrayStabilizer = <T>(fingerprint: (xs: readonly T[]) => string) => {
  let cachedFp = '';
  let cached: readonly T[] | null = null;
  return (next: T[]): T[] => {
    const fp = fingerprint(next);
    if (fp === cachedFp && cached !== null) return cached as T[];
    cachedFp = fp;
    cached = next;
    return next;
  };
};

const fingerprintAssets = (
  assets: readonly DraftMapAssetFeatureWithSelection[]
): string =>
  assets
    .map(
      (a) =>
        `${a.id}${STABILIZER_DELIMITER}${a.selected ? 1 : 0}` +
        `${STABILIZER_DELIMITER}${a.subtractIds.join(',')}`
    )
    .sort()
    .join(STABILIZER_DELIMITER);

// Step 1 and Step 2 each get their own array stabilizer (separate
// caches; same fingerprint shape) — both render concurrently from
// independent inputs.
const stabilizeAssets =
  makeArrayStabilizer<DraftMapAssetFeatureWithSelection>(fingerprintAssets);
const stabilizeSelectedOnlyAssets =
  makeArrayStabilizer<DraftMapAssetFeatureWithSelection>(fingerprintAssets);
const stabilizeSlots = makeArrayStabilizer<DraftMapSlotFeature>((slots) =>
  slots
    .map(
      (s) =>
        `${s.id}${STABILIZER_DELIMITER}` +
        `${s.survey ? 1 : 0}${s.stick ? 1 : 0}` +
        `${s.estimatedProducingSegment ? 1 : 0}` +
        `${s.surfaceHole ? 1 : 0}${s.bottomHole ? 1 : 0}`
    )
    .sort()
    .join(STABILIZER_DELIMITER)
);
const stabilizeDAs = makeArrayStabilizer<DraftMapDevelopmentAreaFeature>(
  (das) =>
    das
      .map((d) => d.name)
      .sort()
      .join(STABILIZER_DELIMITER)
);

/**
 * Wrapper-object stabilizer. Mirrors {@link makeArrayStabilizer} for
 * `WizardMapData` so a recompute that produces a structurally-equal
 * wrapper returns the previous reference; without this, Jotai notifies
 * subscribers on every recompute even when every field is unchanged.
 *
 * @param {Function} equals - Field-by-field equality predicate
 * @return {Function} A stabilizer that returns the cached wrapper on equality
 */
const makeWizardMapDataStabilizer = (
  equals: (a: WizardMapData, b: WizardMapData) => boolean
) => {
  let cached: WizardMapData | null = null;
  return (next: WizardMapData): WizardMapData => {
    if (cached !== null && equals(cached, next)) return cached;
    cached = next;
    return next;
  };
};

const wizardMapDataEqual = (a: WizardMapData, b: WizardMapData): boolean =>
  a.assets === b.assets &&
  a.slots === b.slots &&
  a.developmentAreas === b.developmentAreas &&
  a.features === b.features &&
  a.isLoading === b.isLoading;

const stabilizeWizardMapData = makeWizardMapDataStabilizer(wizardMapDataEqual);
const stabilizeWizardMapSelectedOnlyData =
  makeWizardMapDataStabilizer(wizardMapDataEqual);

/**
 * Test-only escape hatch. Vitest runs all tests in a file against the
 * same module instance, so the stabilizer caches persist across tests
 * and can leak state. Call this in a `beforeEach` to reset the wizard-
 * map atom caches between tests.
 *
 * @return {void}
 */
export const __resetWizardMapCachesForTest = (): void => {
  stabilizeAssets([]);
  stabilizeSlots([]);
  stabilizeDAs([]);
  // Force the wrapper stabilizers' caches into a known empty state.
  // Reading the same wrapper twice with disjoint fields invalidates
  // both caches.
  const sentinelA: WizardMapData = {
    assets: [],
    slots: [],
    developmentAreas: [],
    features: null,
    isLoading: false,
  };
  const sentinelB: WizardMapData = { ...sentinelA, isLoading: true };
  stabilizeWizardMapData(sentinelA);
  stabilizeWizardMapData(sentinelB);
  stabilizeWizardMapSelectedOnlyData(sentinelA);
  stabilizeWizardMapSelectedOnlyData(sentinelB);
};

/**
 * Draws a tick the server answered 200 on the Step 1 map before `maps/features`
 * is read again: an unticked Tract is no longer drawn as held, and a ticked one
 * is drawn from its row on the Assets table page on hand. A ticked asset missing
 * from that page (the table moved on meanwhile) waits for that read.
 */
export const flipDraftMapSelectionAtom = atom(
  null,
  (
    get,
    set,
    {
      addedAssetIds = [],
      removedAssetIds = [],
    }: { addedAssetIds?: string[]; removedAssetIds?: string[] }
  ) => {
    const page = get(draftAssetsQueryLoadableAtom);
    const rows = page.state === 'hasData' ? (page.data?.results ?? []) : [];
    const flips = new Map(get(draftMapSelectionFlipsAtom));
    for (const id of addedAssetIds) {
      const row = rows.find((candidate) => draftAssetRowId(candidate) === id);
      // A wellbore or LTD asset has no shape of its own on the map.
      if (row?.salesforceId && row.geometry !== undefined) {
        flips.set(id, { id, geometry: row.geometry, subtractIds: [] });
      }
    }
    for (const id of removedAssetIds) flips.set(id, null);
    set(draftMapSelectionFlipsAtom, flips);
  }
);

export const wizardMapDataAtom = atom<WizardMapData>((get) => {
  const featuresState = get(draftMapFeaturesLoadableAtom);
  const flips = get(draftMapSelectionFlipsAtom);
  const tableAssetsState = get(draftAssetsQueryLoadableAtom);
  const tableSlotsState = get(draftSlotsQueryLoadableAtom);

  const features =
    featuresState.state === 'hasData' ? featuresState.data : null;
  const tableAssets =
    tableAssetsState.state === 'hasData' ? tableAssetsState.data : null;
  const tableSlots =
    tableSlotsState.state === 'hasData' ? tableSlotsState.data : null;

  const isLoading =
    features === null ||
    tableAssetsState.state === 'loading' ||
    tableSlotsState.state === 'loading';

  const assetsMap = new Map<string, DraftMapAssetFeatureWithSelection>();
  const slotsMap = new Map<string, DraftMapSlotFeature>();
  const dasMap = new Map<string, DraftMapDevelopmentAreaFeature>();

  if (tableAssets) {
    for (const row of tableAssets.results) {
      // Geometry only ships on tract rows; non-tract rows have no
      // polygon/footprint to surface on the wizard map.
      if (row.geometry === undefined || !row.salesforceId) continue;
      assetsMap.set(row.salesforceId, {
        id: row.salesforceId,
        geometry: row.geometry,
        subtractIds: [],
        selected: row.selected,
      });
    }
  }
  if (tableSlots) {
    for (const slot of tableSlots.slots) {
      if (slot.geometry !== undefined) {
        slotsMap.set(slot.slotId, {
          id: slot.slotId,
          survey: slot.geometry.survey,
          stick: slot.geometry.stick,
          surfaceHole: slot.geometry.surfaceHole,
          bottomHole: slot.geometry.bottomHole,
          estimatedProducingSegment: slot.geometry.estimatedProducingSegment,
        });
      }
      if (slot.developmentArea !== undefined) {
        dasMap.set(slot.developmentArea.name, {
          name: slot.developmentArea.name,
          geometry: slot.developmentArea.geometry,
        });
      }
    }
  }

  if (features) {
    // `features.assets` is selected-only by BE contract — pin
    // `selected: true` here so the wire shape doesn't have to carry it. A
    // Tract ticked or unticked since the read is drawn as the tick left it.
    for (const a of features.assets) {
      if (!flips.has(a.id)) assetsMap.set(a.id, { ...a, selected: true });
    }
    for (const s of features.slots) slotsMap.set(s.id, s);
    for (const d of features.developmentAreas) dasMap.set(d.name, d);
  }
  for (const [id, ticked] of flips) {
    const row = assetsMap.get(id);
    if (ticked) assetsMap.set(id, { ...ticked, selected: true });
    else if (row) assetsMap.set(id, { ...row, selected: false });
  }

  const assets = stabilizeAssets(
    assetsMap.size === 0 ? NO_ASSET_FEATURES : Array.from(assetsMap.values())
  );
  const slots = stabilizeSlots(
    slotsMap.size === 0 ? NO_SLOT_FEATURES : Array.from(slotsMap.values())
  );
  const developmentAreas = stabilizeDAs(
    dasMap.size === 0 ? NO_DA_FEATURES : Array.from(dasMap.values())
  );

  // Stabilize the wrapper too — Jotai notifies subscribers whenever the
  // atom function returns a non-Object.is-equal value, so a fresh object
  // here would still re-render every consumer even if every field is
  // referentially unchanged.
  //
  // The `features === features` check inside `wizardMapDataEqual` assumes
  // Jotai's loadable keeps `featuresState.data` referentially stable
  // across reads with the same resolved promise — true today; if
  // `mutationAtomWithLoadable` ever re-wraps on each get, this short-
  // circuit silently no-ops and the wrapper rebuilds every time. The
  // atoms-test reference-equality spec guards against that regression.
  return stabilizeWizardMapData({
    assets,
    slots,
    developmentAreas,
    features,
    isLoading,
  });
});

/**
 * Step 2 (Summary) variant of {@link wizardMapDataAtom} — reads only the
 * `maps/features` selected universe and skips the table-side merge. Step 2
 * doesn't allow selection changes; the table loadables don't contribute
 * geometry, so subscribing to them would just waste compute on every Step 1
 * page/filter change. `isLoading` here is narrower than the merged atom's
 * — it's `features === null`, not table-loading-aware.
 */
export const wizardMapSelectedOnlyDataAtom = atom<WizardMapData>((get) => {
  const featuresState = get(draftMapFeaturesLoadableAtom);
  const features =
    featuresState.state === 'hasData' ? featuresState.data : null;
  // `features.assets` is selected-only by BE contract — pin `selected: true`
  // here so downstream paint expressions can read a uniform property.
  const assets = stabilizeSelectedOnlyAssets(
    features?.assets
      ? features.assets.map((a) => ({ ...a, selected: true }))
      : NO_ASSET_FEATURES
  );
  return stabilizeWizardMapSelectedOnlyData({
    assets,
    slots: features?.slots ?? NO_SLOT_FEATURES,
    developmentAreas: features?.developmentAreas ?? NO_DA_FEATURES,
    features,
    isLoading: features === null,
  });
});

/**
 * Slot-selection mutation atom for Step 1's slots table. PATCHes
 * `/packages/drafts/:draft_id/slot_selections` in any of the three modes
 * the BE Updater accepts (individual / select_all / select_all_filtered);
 * the mode is inferred from which fields the payload carries. Callers
 * refetch slots on success so `meta.selectionState` reflects the new
 * cross-page truth.
 */
export const [updateSlotSelectionAtom, updateSlotSelectionLoadableAtom] =
  mutationAtomWithLoadable<
    SlotSelectionResponse,
    [SlotSelectionMutationPayload]
  >(async (_get, set, payload) => {
    const { draftId, ...body } = payload;
    const response = await request.patch<
      SlotSelectionResponse,
      DraftChangeFailure
    >(
      `/packages/drafts/${draftId}/slot_selections`,
      body,
      new AbortController()
    );
    if (response.type === 'success') {
      set(currentDraftSummaryAtom, {
        asset: response.data.assetSummary,
        slot: response.data.slotSummary,
      });
      return response.data;
    }
    throw draftChangeError(response, 'Failed to update property selection');
  });

/**
 * Create-package mutation fired on Step 2 Next. POSTs to `/packages`
 * with a wizard-mode payload (`draft_id` activates Creator's wizard
 * branch). `PackagesController` uses `params.require(:package).permit`
 * without `deep_transform_keys`, so the wire body is snake_case.
 *
 * Errors: BE emits `{ errors: { field: [msgs] } }` in both paths —
 * `format_creator_errors` and `format_record_invalid_errors`. The
 * AFE-style indexed case encodes the index in the key
 * (`packageAssets[0]`), not as nested objects, so flattening the values
 * one level is enough. We join the messages for a single banner line
 * — per-field inline errors can come with form-level error integration.
 */
export const [createPackageAtom, createPackageLoadableAtom] =
  mutationAtomWithLoadable<CreatePackageResponse, [CreatePackageWizardPayload]>(
    async (_get, _set, payload: CreatePackageWizardPayload) => {
      const response = await request.post<
        CreatePackageResponse,
        { errors: Record<string, string[]> }
      >('/packages', payload, new AbortController());
      if (response.type === 'success') return response.data;
      if (response.type === 'error' && response.data?.errors) {
        const messages = Object.values(response.data.errors).flat();
        if (messages.length > 0) throw new Error(messages.join(', '));
      }
      throw new Error('Failed to create package');
    }
  );

/**
 * Update-package mutation for Step 2's edit-mode Save. PATCH
 * `/packages/:id` with the same wizard-mode payload as create
 * (`draft_id` plus top-level fields). The BE's `Packages::Updater`
 * runs the wizard-diff branch when `draft_id` is present, syncs the
 * draft's selection state into the package, and destroys the draft.
 * Synchronous — returns 200 with the updated package; no Pusher.
 */
export const [updatePackageAtom, updatePackageLoadableAtom] =
  mutationAtomWithLoadable<
    CreatePackageResponse,
    [{ packageId: string; payload: Partial<SavePackagePayload> }]
  >(async (_get, _set, { packageId, payload }) => {
    const response = await request.patch<
      CreatePackageResponse,
      { errors: Record<string, string[]> }
    >(`/packages/${packageId}`, payload, new AbortController());
    if (response.type === 'success') return response.data;
    if (response.type === 'error' && response.data?.errors) {
      const messages = Object.values(response.data.errors).flat();
      if (messages.length > 0) throw new Error(messages.join(', '));
    }
    throw new Error('Failed to update package');
  });

/**
 * Map focus target — at most one entity (asset or slot) is focused at
 * a time. Pinning a slot row clears any focused asset and vice versa.
 * Read by `PackageWizardMap` to drive the camera fit + paint expressions;
 * written by the asset and slot tables' pin-action columns.
 */
export type FocusedMapEntity =
  { kind: 'asset'; id: string } | { kind: 'slot'; id: string };

export const focusedMapEntityAtom = atom<FocusedMapEntity | null>(null);

/**
 * Read-only narrowing of {@link focusedMapEntityAtom} to the asset id
 * (or null when no asset is focused). Both the wizard map and the
 * Step 1 assets table read this; deriving once via the atom layer
 * collapses the `?.kind === 'asset'` ternary repeated at each call site.
 */
export const focusedAssetIdAtom = atom<string | null>((get) => {
  const focused = get(focusedMapEntityAtom);
  return focused?.kind === 'asset' ? focused.id : null;
});

/**
 * Read-only narrowing of {@link focusedMapEntityAtom} to the slot id.
 * Companion to {@link focusedAssetIdAtom}.
 */
export const focusedSlotIdAtom = atom<string | null>((get) => {
  const focused = get(focusedMapEntityAtom);
  return focused?.kind === 'slot' ? focused.id : null;
});

/**
 * Pure predicate for "checkbox interaction should be blocked." Covers the
 * loading state of the underlying query (initial fetch OR a stale post-
 * PATCH refetch where the parent's `selection` prop lags DataTable's
 * internal `selectionRef`) AND the in-flight mutation PATCH itself.
 * Exported so unit tests can exercise the predicate directly without
 * standing up a Jotai store; consumers should prefer the bound atoms
 * below.
 *
 * @param {object} queryState - Loadable state of the table query
 * @param {object} updateState - Loadable state of the selection mutation
 * @return {boolean} True when checkboxes should be disabled
 */
export const isSelectionDisabledFromStates = (
  queryState: { state: 'loading' | 'hasData' | 'hasError' },
  updateState: { state: 'loading' | 'hasData' | 'hasError' }
): boolean => queryState.state === 'loading' || updateState.state === 'loading';

/**
 * True when the assets-table checkboxes should be disabled. Step 1
 * AssetsTable additionally ORs in `awaitingDraftMaterializationAtom`
 * so a click in the post-202 Pusher window can't fire another
 * PATCH against a mid-flight draft.
 */
export const assetsSelectionDisabledAtom = atom<boolean>((get) =>
  isSelectionDisabledFromStates(
    get(draftAssetsQueryLoadableAtom),
    get(updateDraftAssetsLoadableAtom)
  )
);

/**
 * True from an asset change's 202 until the build it queued reports back:
 * Step 1's one wait, which holds the whole step. The PATCH has resolved by
 * then, so its loadable no longer shows it. Set by `useDraftRebuild`.
 */
export const awaitingDraftMaterializationAtom = atom(false);

/** How Step 1's last wait for a build ended, when not ready; null otherwise. */
export const draftRebuildFailureAtom = atom<DraftRebuildFailure | null>(null);

/**
 * Bumped once a change to the draft has settled: a tick, a build an asset
 * change queued, select all or deselect all, a scenario switch, a new wellbore
 * or LTD asset. Step 1's tables and map re-read the draft on each bump, so they
 * show what the server holds.
 */
export const draftReloadAtom = atom(0);

/**
 * Bumped once the server refused a change: Step 1's tables re-read the draft,
 * and the map does not. The change changed nothing, and the tables read again
 * only to drop the Subtract ticks they showed for it.
 */
export const draftTablesReloadAtom = atom(0);

/**
 * Bumped with `draftReloadAtom` once a build an asset change queued, or a
 * scenario switch, has ended (a wait that timed out has not seen its build
 * end). The Step 1 map fits its camera to what that re-read brings, and the
 * clone notices are read again; the re-read after a tick does neither.
 */
export const draftRebuildsEndedAtom = atom(0);

/**
 * True while the Step 1 assets table holds Subtract ticks back for a short
 * pause, to send them as one asset change. Set by that table.
 */
export const subtractTicksPendingAtom = atom(false);

/**
 * True while an asset change is waiting to be sent, in flight or still
 * building. The server refuses any other asset or slot selection change until
 * then, so the slots table holds its per-row Remove and its selection.
 */
export const draftAssetChangePendingAtom = atom<boolean>(
  (get) =>
    get(subtractTicksPendingAtom) ||
    get(updateDraftAssetsLoadableAtom).state === 'loading' ||
    get(awaitingDraftMaterializationAtom)
);

/** True when the slots-table checkboxes should be disabled. */
export const slotsSelectionDisabledAtom = atom<boolean>((get) =>
  isSelectionDisabledFromStates(
    get(draftSlotsQueryLoadableAtom),
    get(updateSlotSelectionLoadableAtom)
  )
);

/**
 * Write-only atom for the asset/slot pin click. Sets `target` as the
 * focused entity, or clears focus if `target` already matches the
 * current entity (toggle-off behavior). Tables consume the same helper
 * so the same-entity-clears-focus rule lives in one place.
 */
export const togglePinAtom = atom(
  null,
  (get, set, target: FocusedMapEntity) => {
    const prev = get(focusedMapEntityAtom);
    set(
      focusedMapEntityAtom,
      prev?.kind === target.kind && prev.id === target.id ? null : target
    );
  }
);

/**
 * Package wizard map config, persisted to localStorage so layer toggles
 * (basin tier, drilling rigs, etc.) survive across sessions. Follows the
 * `afeMapConfigAtom` precedent in `pages/afe-catalog/new/atoms.ts`.
 *
 * Default layers are all disabled. Selected-asset/slot polygon rendering
 * is currently a no-op in `PackageWizardMap` — it'll resume once BE-driven
 * geometry lands.
 *
 * Bump the `-v1` storage key if layer defaults change in a way that
 * existing users shouldn't inherit.
 */
export const packageMapConfigAtom = atomWithStorage(
  'insights-package-wizard-map-config-v1',
  getMapDefaultConfig()
);

/**
 * Feature-local pool of NRI/WI verification edits, keyed by
 * `${nriCalculationId}-${type}`. The lib `verificationStateAtomFamily`
 * (`lib/atoms/verification`) is intentionally not used — cross-surface
 * bleed isn't desired on the package wizard.
 *
 * Hydrated by the slot table on each fetch (via `useHydrateVerifications`);
 * written by the cell on user interaction (via `useNriVerification`);
 * read by both cells and parent-row rollups (via `useNriRollup`).
 */
export const verificationStatesAtom = atom<Record<string, VerificationState>>(
  {}
);

/**
 * The asset list's name check: every answer so far, the matched Tracts'
 * preview rows, and the last failure. Answers are kept across list edits so a
 * name is checked once; picking another source tile clears it. Scoped by the
 * wizard's `<Provider>`, so it resets on route re-entry.
 */
export const assetNameCheckAtom = atom<AssetNameCheck>(EMPTY_ASSET_NAME_CHECK);
